import { createPartialApiContext, ensurePackageBundled } from '@ms-cloudpack/api-server';
import type { PackageJson } from '@ms-cloudpack/common-types';
import { yellow } from '@ms-cloudpack/task-reporter';
import path from 'path';
import type { CommandAction } from '../../types/CommandAction.js';
import { getFilteredPackages } from '../../utilities/getFilteredPackages.js';
import { InitSummaryData } from '../init/InitSummaryData.js';
import { formatInitSummary } from '../init/formatInitSummary.js';
import type { BundleOptions } from './types/BundleOptions.js';

export const execute: CommandAction<BundleOptions> = async (params) => {
  const { appPath, options, reporter, setInterruptMessageHandler } = params;

  console.log('Bundle mode:', yellow(options.mode));

  const ctx = await createPartialApiContext({
    ...params,
    // If we're only bundling the current package, we don't need to resolve all dependencies.
    onlyInlinedDependencies: !options.match,
  });

  const pathsToBundle = options.match ? getFilteredPackages(ctx.session.resolveMap, options.match) : [appPath];

  if (!pathsToBundle.length) {
    return {
      message: 'No matching packages found to bundle.',
      hasErrors: true,
    };
  }

  // Show completed bundle tasks so the user knows what's happening.
  reporter.setOptions({ ...reporter.getOptions(), showCompleted: true });

  // Use InitSummaryData for pretty formatting of errors and warnings at the end.
  const summaryData = new InitSummaryData({ appPath });

  for (const packagePath of pathsToBundle) {
    let definition: PackageJson | undefined;
    try {
      // If outdir is set, resolve relative to the package/app path.
      // Otherwise, use the default path under the cloudpack cache to stay consistent with init and start,
      // and to avoid potentially messing with the package's original output.
      const outputPath = options.outdir ? path.resolve(packagePath, options.outdir) : undefined;

      definition = await ctx.packages.get(packagePath);

      const { result } = await ensurePackageBundled(
        {
          name: definition.name,
          version: definition.version,
          outputPath,
          disableSourceMaps: options.disableSourceMaps,
          shouldForce: true,
          isIncremental: false,
          shouldGetBundleInfo: options.logBundleInfo,
        },
        ctx,
      );

      console.log('Output:', result.outputPath, '\n');

      summaryData.recordResult(packagePath, result);
    } catch (err) {
      summaryData.recordException({
        packagePath,
        error: err,
        source: 'bundle',
        context: 'bundling package',
        name: definition?.name,
        version: definition?.version,
      });
    }
  }

  setInterruptMessageHandler(() =>
    formatInitSummary({ verb: 'bundle', summary: summaryData.summarize(), isInterrupted: true }),
  );

  const summary = summaryData.summarize();

  return {
    hasErrors: summary.failed,
    message: formatInitSummary({ verb: 'bundle', summary }),
  };
};
